import { Injectable } from '@angular/core';
import {
  PatientBehaviorModel,
  PatientPlaylistModel,
  PatientProgramModel,
  PatientTargetModel,
} from '../api/models';

const STORAGE_KEY = 'spark-light-load';
const FORCE_FULL_REFRESH_KEY = 'spark-force-full-refresh';

/**
 * Light Load keeps only what is required to take session data.
 * It is never the default. Opto App iOS turns it on only after WKWebView
 * WebContent was killed (memory pressure), then the next Spark load
 * passes lightLoad=1. Users can turn it off from the Spark menu.
 */
@Injectable({ providedIn: 'root' })
export class SparkLightLoadService {
  private enabled = false;

  constructor() {
    this.enabled = this.readInitial();
    (window as any).__sparkLightLoad = this.enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    const turningOff = this.enabled && !enabled;
    this.enabled = enabled;
    (window as any).__sparkLightLoad = enabled;
    try {
      if (enabled) {
        localStorage.setItem(STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(STORAGE_KEY);
        this.removeLightLoadQueryParam();
        if (turningOff) {
          this.markForceFullRefresh();
        }
      }
    } catch {
      // Private mode / WK storage eviction.
    }
  }

  shouldForceFullSnapshot(): boolean {
    if (this.enabled) {
      return false;
    }
    try {
      return localStorage.getItem(FORCE_FULL_REFRESH_KEY) === '1';
    } catch {
      return false;
    }
  }

  clearForceFullSnapshot(): void {
    try {
      localStorage.removeItem(FORCE_FULL_REFRESH_KEY);
    } catch {
      // ignore
    }
  }

  markForceFullRefresh(): void {
    try {
      localStorage.setItem(FORCE_FULL_REFRESH_KEY, '1');
    } catch {
      // ignore
    }
  }

  stripTarget(target: PatientTargetModel): PatientTargetModel {
    if (!this.enabled || !target) {
      return target;
    }
    target.Instructions = null;
    target.TargetNotes = null;
    target.TreatmentGoals = null;
    target.IsTargetNoteNew = false;
    target.NotesLastModifiedByUserFullName = null;
    target.NotesLastModifiedDate = null;
    return target;
  }

  stripTargets(targets: PatientTargetModel[] | null | undefined): PatientTargetModel[] {
    if (!targets) {
      return [];
    }
    if (!this.enabled) {
      return targets;
    }
    targets.forEach((target) => this.stripTarget(target));
    return targets;
  }

  stripProgram(program: PatientProgramModel): PatientProgramModel {
    if (!this.enabled || !program) {
      return program;
    }
    program.Details = null;
    program.ProgramNotes = null;
    program.TreatmentGoals = null;
    program.IsProgramNoteNew = false;
    program.NotesLastModifiedByUserFullName = null;
    if (program.Targets) {
      this.stripTargets(program.Targets);
    }
    return program;
  }

  stripPrograms(programs: PatientProgramModel[] | null | undefined): PatientProgramModel[] {
    if (!programs) {
      return [];
    }
    if (!this.enabled) {
      return programs;
    }
    programs.forEach((program) => this.stripProgram(program));
    return programs;
  }

  stripBehavior(behavior: PatientBehaviorModel): PatientBehaviorModel {
    if (!this.enabled || !behavior) {
      return behavior;
    }
    behavior.OperationalDefinition = null;
    return behavior;
  }

  stripBehaviors(behaviors: PatientBehaviorModel[] | null | undefined): PatientBehaviorModel[] {
    if (!behaviors) {
      return [];
    }
    if (!this.enabled) {
      return behaviors;
    }
    behaviors.forEach((behavior) => this.stripBehavior(behavior));
    return behaviors;
  }

  stripPlaylists(playlists: PatientPlaylistModel[] | null | undefined): PatientPlaylistModel[] {
    if (!playlists) {
      return [];
    }
    if (!this.enabled) {
      return playlists;
    }
    playlists.forEach((playlist) => {
      playlist.Details = null;
    });
    return playlists;
  }

  private removeLightLoadQueryParam(): void {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('lightLoad')) {
        return;
      }
      url.searchParams.delete('lightLoad');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // ignore
    }
  }

  private readInitial(): boolean {
    if ((window as any).__sparkLightLoad === true) {
      return true;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') {
        return true;
      }
    } catch {
      // ignore
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('lightLoad') === '1') {
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }
}
